import { DeleteFilled, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Col, Result, Row, Space, Spin, App as AntApp } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { deleteBillableOption, fetchBillableOptionById } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const BillableLookupListDetail = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const { modal } = AntApp.useApp();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const isLoading =
        lookupSlice.detailsPage.billableOption.updateStatus === StatePieceStatus.IsFetching ||
        lookupSlice.detailsPage.billableOption.fetchStatus === StatePieceStatus.IsFetching;
    const itemId = params.id ? parseInt(params.id) : undefined;

    const removeBillableOption = () => {
        modal.confirm({
            title: 'Delete billable option',
            icon: <ExclamationCircleOutlined />,
            content: "Are you sure you want to delete this billable option? This action can't be undone.",
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => {
                if (itemId) dispatch(deleteBillableOption(itemId));
            },
        });
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.billableOption.deleteStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/billable', { replace: true });
        }
    }, [lookupSlice.detailsPage.billableOption.deleteStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchBillableOptionById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    if (lookupSlice.detailsPage.billableOption.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <React.Fragment>
            <ContentHeader
                title={`Billable option #${params.id}`}
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Billable option',
                        path: '/admin/lookup-lists/billable',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.billableOption.data?.description || '-',
                        path: `/admin/lookup-lists/billable/${params.id}`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.billableOption.fetchStatus) && (
                    <>
                        <Button type="primary" danger icon={<DeleteFilled />} loading={isLoading} onClick={() => removeBillableOption()}>
                            Delete option
                        </Button>
                        <Button
                            type="default"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/admin/lookup-lists/billable/${params.id}/edit`)}
                        >
                            Edit option details
                        </Button>
                    </>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.billableOption.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.billableOption.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.billableOption.fetchStatus} about={'Billable'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Billable option Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Id</label>
                                    <p>{lookupSlice.detailsPage.billableOption?.data?.id || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <p>{lookupSlice.detailsPage.billableOption?.data?.description || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Default</label>
                                    <p>{lookupSlice.detailsPage.billableOption?.data?.isDefault ? 'Yes' : 'No'}</p>
                                </Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.billableOption.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.billableOption.data?.created
                                            ? dayjs(lookupSlice.detailsPage.billableOption.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.billableOption.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.billableOption.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.billableOption.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </React.Fragment>
    );
};
